import { css } from 'styled-components';

export const baseStyles = css`
  :root {
    --btn-loading-bg: ${({ theme }) =>
      theme.mode === 'dark'
        ? theme.colors.surfaceRaised
        : theme.colors.neutral[300]};
    --btn-loading-color: ${({ theme }) => theme.colors.text.muted};
  }

  * {
    max-width: 100vw;
    box-sizing: border-box;
    padding: 0;
    margin: 0;
    font-family: ${({ theme }) => theme.fontFamily};
  }

  /* Hide caret on non-input elements globally */
  * {
    caret-color: transparent;
  }

  input,
  textarea,
  [contenteditable] {
    caret-color: ${({ theme }) => theme.colors.text.primary};
  }

  /* Accessibility: visible focus for keyboard navigation */
  *:focus {
    outline: none;
  }

  *:focus-visible {
    outline: none;
    box-shadow: ${({ theme }) => theme.focus.ring};
    border-radius: ${({ theme }) => theme.borderRadius.sm};
  }

  /* Skip to content link for screen readers */
  .skip-to-content {
    position: absolute;
    top: -100%;
    left: 50%;
    transform: translateX(-50%);
    z-index: 9999;
    padding: ${({ theme }) => theme.spacing[3]}
      ${({ theme }) => theme.spacing[5]};
    background-color: ${({ theme }) => theme.colors.primary.base};
    color: #ffffff;
    font-weight: ${({ theme }) => theme.fontWeight.semiBold};
    font-size: ${({ theme }) => theme.fontSize.base};
    border-radius: 0 0 ${({ theme }) => theme.borderRadius.md}
      ${({ theme }) => theme.borderRadius.md};
    text-decoration: none;
    transition: top ${({ theme }) => theme.transition.fast};

    &:focus {
      top: 0;
    }
  }

  body {
    box-sizing: border-box;
    max-width: 100vw;
    font-weight: ${({ theme }) => theme.fontWeight.light};
    overflow-x: hidden;
    font-family: ${({ theme }) => theme.fontFamily};
    background-color: ${({ theme }) => theme.colors.bg};
    color: ${({ theme }) => theme.colors.text.primary};
    transition:
      background-color ${({ theme }) => theme.transition.slow},
      color ${({ theme }) => theme.transition.slow};
  }

  ::-webkit-scrollbar {
    appearance: none;
    width: 8px;
    height: 8px;
  }
  ::-webkit-scrollbar-thumb {
    background-color: ${({ theme }) => theme.colors.scrollbar.thumb};
    border-radius: 5px;

    &:hover {
      background-color: ${({ theme }) =>
        theme.colors.scrollbar.thumb.replace('0.5', '0.7')};
    }
  }
  ::-webkit-scrollbar-track {
    background-color: ${({ theme }) => theme.colors.scrollbar.track};
    border-radius: 5px;
  }

  /* Firefox scrollbar */
  * {
    scrollbar-width: thin;
    scrollbar-color: ${({ theme }) => theme.colors.scrollbar.thumb}
      ${({ theme }) => theme.colors.scrollbar.track};
  }

  input:not(.ant-picker input),
  textarea {
    padding: 0 ${({ theme }) => theme.spacing[3]};
    width: 100%;
    margin-bottom: 0px;
    border: 1px solid ${({ theme }) => theme.colors.border};
    border-radius: ${({ theme }) => theme.borderRadius.base};
    background-color: ${({ theme }) => theme.colors.surface};
    color: ${({ theme }) => theme.colors.text.primary};
    font-family: ${({ theme }) => theme.fontFamily};
    font-size: ${({ theme }) => theme.fontSize.base};
    transition:
      border-color ${({ theme }) => theme.transition.base},
      background-color ${({ theme }) => theme.transition.base},
      box-shadow ${({ theme }) => theme.transition.base};

    &:focus {
      border-color: ${({ theme }) => theme.colors.primary.base};
    }

    &:focus-visible {
      box-shadow: ${({ theme }) => theme.focus.ring};
    }

    &::placeholder {
      color: ${({ theme }) => theme.colors.text.muted};
    }
  }

  input:not(.ant-picker input) {
    height: 40px;
    line-height: 40px;

    &:disabled {
      background-color: ${({ theme }) =>
        theme.mode === 'dark'
          ? theme.colors.surfaceRaised
          : theme.colors.neutral[200]};
      border: 1px solid ${({ theme }) => theme.colors.border};
      cursor: not-allowed;
      color: ${({ theme }) => theme.colors.text.muted};
    }
  }

  textarea {
    padding-top: ${({ theme }) => theme.spacing[2]};
    padding-bottom: ${({ theme }) => theme.spacing[2]};
    min-height: 80px;
    resize: vertical;
  }

  a,
  img {
    display: block;
  }

  /* Autofill: force theme colors instead of browser default yellow/blue */
  input:not(.ant-picker input):-webkit-autofill,
  input:not(.ant-picker input):-webkit-autofill:hover,
  input:not(.ant-picker input):-webkit-autofill:focus,
  input:not(.ant-picker input):-webkit-autofill:active,
  textarea:-webkit-autofill,
  textarea:-webkit-autofill:hover,
  textarea:-webkit-autofill:focus,
  textarea:-webkit-autofill:active {
    -webkit-box-shadow: 0 0 0 1000px ${({ theme }) => theme.colors.surface}
      inset !important;
    -webkit-text-fill-color: ${({ theme }) =>
      theme.colors.text.primary} !important;
    caret-color: ${({ theme }) => theme.colors.text.primary};
    transition: background-color 5000s ease-in-out 0s;
  }

  input::-webkit-outer-spin-button,
  input::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }

  /* Firefox */
  input[type='number'] {
    -moz-appearance: textfield;
  }

  h5 {
    font-size: ${({ theme }) => theme.fontSize.lg};
  }

  /* Reduced motion: disable all animations */
  @media (prefers-reduced-motion: reduce) {
    *,
    *::before,
    *::after {
      animation-duration: 0.01ms !important;
      animation-iteration-count: 1 !important;
      transition-duration: 0.01ms !important;
      scroll-behavior: auto !important;
    }
  }

  /* Visually hidden utility for screen readers */
  .sr-only {
    position: absolute;
    width: 1px;
    height: 1px;
    padding: 0;
    margin: -1px;
    overflow: hidden;
    clip: rect(0, 0, 0, 0);
    white-space: nowrap;
    border-width: 0;
  }
`;
